/**
 * What a face is called in front of a person.
 *
 * `torso.front` and `rightLimb.up` are correct and nobody should ever read them.
 * The panel says "Chest" and "Right sleeve · top", because the whole argument
 * for face-relative placement is that the interface can talk about a garment
 * instead of about coordinates.
 *
 * Two things verified in the 3D viewer rather than assumed, because getting
 * either wrong puts a logo on the wrong arm and nobody finds out until after
 * they have paid to upload:
 *
 *   - **Every left/right is the avatar's, not the viewer's.** Painted
 *     `torso.front` green to fix orientation, then `rightLimb.front` red and
 *     `leftLimb.front` blue: red landed on the viewer's left, which is an
 *     avatar's right arm when it faces you. Repeated for `torso.left` /
 *     `torso.right` separately — same convention, no exceptions.
 *   - **Legs reuse the limb regions.** `setSurface("legs", …)` lit the same
 *     `rightLimb.*` rectangles, so the noun depends on the garment. Sleeve for a
 *     shirt, leg for pants. That is exactly why this lives in the UI layer and
 *     not in the schema.
 */
import type { GarmentType, RegionId } from "../../shared/format";

const TORSO: Record<string, string> = {
  front: "Chest",
  back: "Back",
  left: "Left side",
  right: "Right side",
  up: "Shoulders",
  down: "Waist",
};

/**
 * Limb facets stay literal.
 *
 * "Inner" and "outer" would read better — the face against the body versus the
 * one facing the world — but which of `left`/`right` is outer flips between the
 * two limbs, and that is a claim about Roblox's axes I have not verified. A
 * plainer word that is certainly right beats a nicer one that might not be.
 */
const FACET: Record<string, string> = {
  front: "front",
  back: "back",
  left: "left",
  right: "right",
  up: "top",
  down: "bottom",
};

function limbNoun(garment: GarmentType): string {
  return garment === "pants" ? "leg" : "sleeve";
}

function split(face: RegionId): { part: string; facet: string } {
  const dot = face.indexOf(".");
  return { part: face.slice(0, dot), facet: face.slice(dot + 1) };
}

/** The group a face belongs to: "Chest", "Right sleeve". */
export function partLabel(face: RegionId, garment: GarmentType = "shirt"): string {
  const { part, facet } = split(face);
  if (part === "torso") return TORSO[facet] ?? "Torso";
  const side = part === "rightLimb" ? "Right" : "Left";
  return `${side} ${limbNoun(garment)}`;
}

/** The full name, for anywhere a face needs to be unambiguous. */
export function faceLabel(face: RegionId, garment: GarmentType = "shirt"): string {
  const { part, facet } = split(face);
  // A torso face is already unique — "Chest · front" would be noise.
  if (part === "torso") return TORSO[facet] ?? face;
  return `${partLabel(face, garment)} · ${FACET[facet] ?? facet}`;
}
